/** @jsxImportSource @emotion/react */
import { FiltersSchema } from '@vitafy/search-data-api-contracts';
import { useRouter } from 'next/router';
import { useRef } from 'react';

import { getLayoutData } from '@backend/layoutData';
import { isStoreCode } from '@common/typeCheckers';
import { ProductList } from '@stories/templates/Search';
import { mq } from '@stories/theming/settings';
import productListDefaultSettings from '@utils/data/ProductListDefaultSettings';
import { getSearchClient, setupRequests } from '@utils/helpers';
import { getChildRequestLogger } from '@utils/logger';
import { ProductListContext, useProductList } from '@utils/productList';

import Layout from '../../components/layout';

import { reduceProductListResponse } from './category/[categoryId]';

import type { LayoutProps } from '../../components/layout';
import type { NextPageWithLayout } from '../_app';
import type { ActiveFiltersAndSorting, productsAndAvailableFilters, sortingOrder } from '@utils/productList';
import type { GetServerSideProps } from 'next';

setupRequests();

type TSearchRouteParams = {
  searchTerm: string;
  store: string;
};

type TSearchState = LayoutProps;

export const getServerSideProps: GetServerSideProps<TSearchState, TSearchRouteParams> = async (context) => {
  const { req, params, query } = context;
  const log = getChildRequestLogger(req, params, query);
  const { store: storeCode } = params || {};

  if (!storeCode || !isStoreCode(storeCode)) {
    log.error('Invalid params: store');
    throw new Error('Invalid params: store');
  }

  const {
    filter,
    page,
    sort,
    q: searchTerm
  } = query as {
    q: string | undefined;
    filter: string | undefined;
    page: string | undefined;
    sort: sortingOrder | undefined;
  };

  const parsedFilters = FiltersSchema.safeParse(JSON.parse(filter ?? '{}'));

  const searchClient = await getSearchClient();

  const searchResponse = await searchClient.searchProducts({
    query: {
      storeCode,
      terms: searchTerm || '',
      filters: parsedFilters.success ? parsedFilters.data : undefined,
      page: page ? Number.parseInt(page) : undefined,
      resultsPerPage: 50, // TODO: use variable or make it configurable
      sort,
      debug: false
    }
  });

  if (searchResponse.status !== 200) {
    log.error(
      {
        searchResponse
      },
      `Can't search products. Due to search: ${searchResponse.status} status`
    );
    throw new Error(`Can't search products.`);
  }

  try {
    const layoutProps = await getLayoutData({
      context,
      storeCode,
      pagePath: '/search',
      pageType: 'product',
      pageMeta: {
        title: 'Suchergebnisse',
        metaDescription: '',
        metaTitle: 'Suchergebnisse',
        metaKeywords: null,
        metaRobots: 'NOINDEX'
      }
    });

    return {
      props: {
        ...layoutProps,
        initialSearchState: reduceProductListResponse(searchResponse.body)
      }
    };
  } catch (error) {
    log.error('Error while fetching layout data', error);
    // Render 500 page
    throw error;
  }
};

type TSearchPageProps = {
  storeCode: string;
  initialSearchState: productsAndAvailableFilters;
} & LayoutProps;

const SearchPage: NextPageWithLayout<TSearchPageProps> = ({ storeCode, initialSearchState }) => {
  const productListRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const searchTerm = router.query.q as string;

  const fetchNextState = async ({
    activeFilters,
    currentPage,
    currentSortingOrder
  }: ActiveFiltersAndSorting) => {
    const searchClient = await getSearchClient();
    const searchResponse = await searchClient.searchProducts({
      query: {
        storeCode,
        terms: searchTerm || '',
        filters: activeFilters,
        page: currentPage,
        sort: currentSortingOrder,
        resultsPerPage: 50, // TODO: use variable or make it configurable
        debug: new URLSearchParams(window.location.search)?.get('debug') === 'alcoholia'
      }
    });
    if (searchResponse.status !== 200) {
      throw new Error(`Can't search products.`);
    }
    return reduceProductListResponse(searchResponse.body);
  };

  const { activeFiltersAndSorting, productsAndAvailableFilters, reducerFunctions } = useProductList({
    initialState: initialSearchState,
    fetchNextState
  });

  return (
    <ProductListContext.Provider
      value={{
        activeFiltersAndSorting,
        productsAndAvailableFilters,
        reducerFunctions,
        defaultSettings: productListDefaultSettings,
        scrollContainerRefIntoView: () => productListRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
    >
      <div ref={productListRef} css={mq({ marginTop: [0, 0, '2rem'] })}>
        <ProductList transitioning={false} />
      </div>
    </ProductListContext.Provider>
  );
};

SearchPage.getLayout = function getLayout(page, pageProps) {
  return <Layout {...pageProps}>{page}</Layout>;
};

export default SearchPage;
